import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, InputNumber, Select, Spin, message } from 'antd'
import { SafetyCertificateOutlined } from '@ant-design/icons'
import {
  getMyVibeDatasetDepartments,
  getMyVibeDatasetDetail,
  saveMyVibeDatasetSettings
} from '../../../request'
import {
  DATASET_IMPORT_MODE_META,
  DATASET_POLICY_META,
  DATASET_UPDATE_FREQUENCY_META,
  getDatasetPolicyMeta,
  normalizeDatasetSettings
} from '../../Profile/vibeDataset'
import { formatDateTime } from '../terminalModel'

// 数据集状态文案与个人中心 Vibe 详情保持一致（Profile/VibeDatasetPanel.js DATASET_STATUS_META）。
const DATASET_STATUS_TEXT = {
  draft: '待首次发布',
  registered: '结构已登记',
  active: '正常',
  disabled: '已停用'
}

/**
 * 操作 tab「数据集设置」分区（owner 或数据 Owner，即 detail.canManage）。
 * 逻辑复制改造自 src/views/Profile/VibeDatasetPanel.js 的 settings 表单，Profile 原文件不动。
 * 保存成功后必须调 onUpdated()，让上层重新拉详情（频率/部门等展示同步刷新）。
 */
export default function DatasetSettingsSection ({ projectId, canManage, onUpdated }) {
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [detail, setDetail] = useState(null)
  const [departments, setDepartments] = useState([])
  const [settings, setSettings] = useState(() => normalizeDatasetSettings())
  const [saving, setSaving] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const loadAll = useCallback(() => {
    if (!canManage || projectId === null || projectId === undefined || projectId === '') return
    setLoading(true)
    setLoadError(false)
    Promise.all([
      getMyVibeDatasetDetail({ projectId }),
      getMyVibeDatasetDepartments({})
    ])
      .then(([detailResult, departmentList]) => {
        if (!mountedRef.current) return
        setDetail(detailResult || null)
        setDepartments(Array.isArray(departmentList) ? departmentList : [])
        setSettings(normalizeDatasetSettings(detailResult))
        setLoading(false)
      })
      .catch(() => {
        if (!mountedRef.current) return
        setLoading(false)
        setLoadError(true)
      })
  }, [canManage, projectId])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  const saveSettings = () => {
    if (saving || projectId === null || projectId === undefined) return
    if (!settings.ownerDepartmentId) {
      message.warning('请选择数据归属一级部门')
      return
    }
    setSaving(true)
    saveMyVibeDatasetSettings({ projectId, ...settings })
      .then(result => {
        if (!mountedRef.current) return
        message.success('数据集设置已保存')
        if (result && typeof result === 'object') {
          setDetail(result)
          setSettings(normalizeDatasetSettings(result))
        }
        if (onUpdated) onUpdated()
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setSaving(false)
      })
  }

  const saveButton = <button
    type="button"
    className="term-primary"
    disabled={saving || loading || loadError}
    onClick={saveSettings}
  >
    {saving ? '保存中…' : '保存'}
  </button>

  if (!canManage) {
    return <section className="term-ops-section">
      <div className="term-ops-head">
        <span className="term-ops-title">数据集设置</span>
      </div>
      <div className="term-ops-card">
        <div className="term-ops-note">仅项目登记人或数据 Owner（上传人）可修改数据集设置。</div>
      </div>
    </section>
  }

  const policy = getDatasetPolicyMeta(settings.sensitivityLevel)

  return <section className="term-ops-section">
    <div className="term-ops-head">
      <span className="term-ops-title">数据集设置</span>
      <span className="term-ops-desc">修改敏感级、归属部门与更新频率后立即生效，接口失败只影响本分区。</span>
      <span className="term-ops-head-action">{saveButton}</span>
    </div>
    <div className="term-ops-card">
      {loadError
        ? <Alert
          type="error"
          showIcon
          message="数据集设置加载失败"
          action={<button type="button" className="term-link" onClick={loadAll}>重试</button>}
        />
        : loading
          ? <div className="term-ops-loading"><Spin size="small" /> 加载中…</div>
          : <React.Fragment>
            <Alert
              type={settings.sensitivityLevel === 'L3' ? 'error' : 'info'}
              showIcon
              icon={<SafetyCertificateOutlined />}
              message={policy.text}
              description={policy.summary}
            />
            <div className="term-ops-form-grid">
              <div className="term-ops-field">
                <div className="term-ops-label">敏感级</div>
                <Select
                  value={settings.sensitivityLevel}
                  style={{ width: '100%' }}
                  onChange={value => updateSetting('sensitivityLevel', value)}
                >
                  {Object.entries(DATASET_POLICY_META).map(([value, item]) => (
                    <Select.Option key={value} value={value}>{item.text}</Select.Option>
                  ))}
                </Select>
              </div>
              <div className="term-ops-field">
                <div className="term-ops-label">数据归属一级部门</div>
                <Select
                  value={settings.ownerDepartmentId}
                  placeholder="请选择数据归属一级部门"
                  style={{ width: '100%' }}
                  onChange={value => updateSetting('ownerDepartmentId', value)}
                >
                  {departments.map(item => (
                    <Select.Option key={String(item.departmentId)} value={item.departmentId}>
                      {item.departmentName || item.departmentId}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className="term-ops-field">
                <div className="term-ops-label">默认导入模式</div>
                <Select
                  value={settings.defaultImportMode}
                  style={{ width: '100%' }}
                  onChange={value => updateSetting('defaultImportMode', value)}
                >
                  {Object.entries(DATASET_IMPORT_MODE_META).map(([value, text]) => (
                    <Select.Option key={value} value={value}>{text}</Select.Option>
                  ))}
                </Select>
              </div>
              <div className="term-ops-field">
                <div className="term-ops-label">更新频率</div>
                <Select
                  value={settings.updateFrequency}
                  style={{ width: '100%' }}
                  onChange={value => updateSetting('updateFrequency', value)}
                >
                  {Object.entries(DATASET_UPDATE_FREQUENCY_META).map(([value, text]) => (
                    <Select.Option key={value} value={value}>{text}</Select.Option>
                  ))}
                </Select>
              </div>
              <div className="term-ops-field">
                <div className="term-ops-label">断更宽限时间（小时）</div>
                <InputNumber
                  min={0}
                  max={720}
                  value={settings.gracePeriodHours}
                  style={{ width: '100%' }}
                  onChange={value => updateSetting('gracePeriodHours', value)}
                />
              </div>
            </div>
            {detail && detail.datasetStatus
              ? <div className="term-ops-meta">
                数据状态：<span className="term-ops-strong">{DATASET_STATUS_TEXT[detail.datasetStatus] || detail.datasetStatus}</span>
                {detail.lastSuccessfulUploadAt
                  ? ` · 最近上传 ${formatDateTime(detail.lastSuccessfulUploadAt)}`
                  : ' · 从未上传过数据'}
              </div>
              : null}
          </React.Fragment>}
    </div>
  </section>
}
